import { type FormEvent, useState } from 'react';
import { Link } from 'react-router';

import { MIN_PASSWORD } from '@/shared/session/password';
import { errorMessage } from '@/shared/api/errors';
import { Badge } from '@/shared/ui/Badge';
import { Button } from '@/shared/ui/Button';
import { Callout } from '@/shared/ui/Callout';
import { Card } from '@/shared/ui/Card';
import { ErrorMessage } from '@/shared/ui/ErrorMessage';
import { Input } from '@/shared/ui/Input';
import { isPlatform, useSessionUser } from '@/shared/session/currentUser';
import { Segmented } from '@/shared/ui/Segmented';
import { Spinner } from '@/shared/ui/Spinner';
import { Table, TBody, Td, Th, THead, Tr } from '@/shared/ui/Table';
import { formatDate } from '@/shared/utils/format';

import { RowMenu } from './RowMenu';
import type { AdminUser, InviteRead } from './types';
import {
  useAdminMailboxes,
  useAdminUsers,
  useCreateUser,
  useInviteUser,
  useResetPassword,
  useUpdateUser,
} from './useAdmin';

const dateFormat = new Intl.DateTimeFormat('es-ES', { dateStyle: 'medium' });
const timeFormat = new Intl.DateTimeFormat('es-ES', { dateStyle: 'medium', timeStyle: 'short' });

/** What the administrator gets to hand over: a link, or a password typed here. */
type Handout =
  { kind: 'invite'; invite: InviteRead } | { kind: 'password'; email: string; password: string };

type HandoutMode = 'invite' | 'password';

/**
 * The mailboxes the system reads: the ones registered in «Buzones» and the one the
 * connector reads from its environment. Contract v10.12: a person sees the mailboxes
 * assigned to them and nothing else, and only these can be assigned. There is no field
 * to type an address: a typo or a mailbox nobody registered is an inbox that stays empty.
 */
function useKnownMailboxes(): string[] {
  const mailboxes = useAdminMailboxes();
  const known = new Set<string>();
  for (const each of mailboxes.data?.items ?? []) known.add(each.name);
  for (const each of mailboxes.data?.environment_mailboxes ?? []) known.add(each);
  return [...known].sort();
}

/**
 * One checkbox per known mailbox. An address already assigned that the system no longer
 * reads (typed by hand before this picker lost its field, or a mailbox deleted since)
 * still shows, marked, so it can be unticked. `onChange` gets the whole list: the API
 * replaces it whole too.
 */
function MailboxPicker({
  label,
  known,
  value,
  disabled,
  onChange,
}: {
  label: string;
  known: string[];
  value: string[];
  disabled?: boolean;
  onChange: (next: string[]) => void;
}) {
  const options = [...new Set([...known, ...value])].sort();

  function toggle(mailbox: string, on: boolean) {
    const next = on ? [...value, mailbox] : value.filter((each) => each !== mailbox);
    onChange([...new Set(next)].sort());
  }

  return (
    <fieldset className="flex flex-col gap-1.5" aria-label={label}>
      {options.length === 0 ? (
        <span className="text-ink-3 text-[12.5px]">
          Ningún buzón dado de alta. Se dan de alta en{' '}
          <Link to="/administracion/buzones" className="link">
            Buzones
          </Link>
          .
        </span>
      ) : null}
      {options.map((mailbox) => (
        <label key={mailbox} className="text-ink-2 flex items-center gap-2 text-[13px]">
          <input
            type="checkbox"
            className="accent-accent size-4"
            checked={value.includes(mailbox)}
            disabled={disabled}
            onChange={(event) => toggle(mailbox, event.target.checked)}
          />
          <span className="flex flex-col">
            <span className="font-mono text-[12.5px]">{mailbox}</span>
            {known.includes(mailbox) ? null : (
              <span className="text-ink-3 text-[12px]">no está dado de alta</span>
            )}
          </span>
        </label>
      ))}
    </fieldset>
  );
}

/**
 * Who enters. Two ways to give somebody their first password (contract v10.10): a link
 * that lasts 24 hours and works once, which the administrator copies and hands over and
 * the person uses to choose their own; or a provisional password the administrator types
 * and shows once, which the person changes on first login. Deactivate, never delete. The
 * API refuses to leave the platform without an Ignira account; the screen just shows the
 * reason it gives.
 *
 * Two tiers (contract v10.11). The role says what mail a person sees; the «Administra»
 * switch says whether they run this panel, whatever the role. Only Ignira (the platform)
 * flips the switch or gives the Ignira role: for anybody else the switch is read-only, and
 * the API would answer 403 regardless. An administrator of the shop does not see the
 * Ignira accounts at all: neither their rows nor the «Superadministrador» column. The back
 * already leaves them out of the list; the filter here covers a back that still sends them.
 *
 * Who gets in is Ignira's call, for now (contract v10.16): the alta form and «Nueva
 * invitación» are only there for a superadministrator. An administrator of the shop
 * edits, deactivates and resets the people already in.
 *
 * The table on the left, the form on the right (under it on a phone). Only the exception
 * is painted in «Estado»: a deactivated person, a first login still pending.
 */
export function UsersTab() {
  const platform = isPlatform(useSessionUser());
  const users = useAdminUsers();
  const [shown, setShown] = useState<Handout | null>(null);

  return (
    <div className="grid items-start gap-4 xl:grid-cols-[1fr_300px]">
      <Card
        title="Personas"
        description="Quién puede entrar y con qué rol. Nadie se borra: se desactiva desde el menú de su fila y deja de poder entrar."
        padded={false}
      >
        {users.isPending ? <Spinner variant="fill" label="Cargando usuarios" /> : null}
        {users.isError ? (
          <ErrorMessage error={users.error} className="m-4">
            No se pudo cargar la lista de personas
          </ErrorMessage>
        ) : null}
        {users.data ? <UsersTable users={users.data.items} onHandout={setShown} /> : null}
      </Card>

      <div className="flex flex-col gap-4">
        {shown?.kind === 'password' ? (
          <Callout tone="ok" title="Contraseña puesta. Se enseña una sola vez.">
            <p>
              <strong>{shown.email}</strong>: <code className="select-all">{shown.password}</code>
            </p>
            <p className="mt-1 text-[13px]">
              Dásela en persona. En su primer acceso tendrá que elegir la suya.
            </p>
          </Callout>
        ) : null}
        {shown?.kind === 'invite' ? <InviteCallout invite={shown.invite} /> : null}
        {platform ? <NewUserForm onCreated={setShown} /> : null}
      </div>
    </div>
  );
}

/**
 * The link, shown once: the API does not keep it anywhere the panel could read it back
 * from. Copied with a button because a 60-character token is not something to select by
 * hand on a phone; the text stays selectable for when the clipboard is not available.
 */
function InviteCallout({ invite }: { invite: InviteRead }) {
  const [copied, setCopied] = useState(false);

  async function copy() {
    try {
      await navigator.clipboard.writeText(invite.link);
      setCopied(true);
    } catch {
      setCopied(false);
    }
  }

  return (
    <Callout
      tone="ok"
      title="Enlace de invitación. Se enseña una sola vez."
      action={
        <Button type="button" size="sm" variant="secondary" onClick={() => void copy()}>
          {copied ? 'Copiado' : 'Copiar enlace'}
        </Button>
      }
    >
      <p>
        Para <strong>{invite.email}</strong>. Vale hasta el{' '}
        {formatDate(timeFormat, invite.expires_at)} y una sola vez.
      </p>
      <p className="mt-1">
        <code className="block break-all select-all">{invite.link}</code>
      </p>
      <p className="mt-1 text-[13px]">
        Pásaselo a esa persona, no a un grupo: quien lo abra elige la contraseña de la cuenta.
        {invite.sent_by_email ? ' También ha salido por correo.' : ''}
      </p>
    </Callout>
  );
}

function UsersTable({
  users,
  onHandout,
}: {
  users: AdminUser[];
  onHandout: (shown: Handout) => void;
}) {
  const me = useSessionUser();
  const platform = isPlatform(me);
  const known = useKnownMailboxes();
  const update = useUpdateUser();
  const reset = useResetPassword();
  const invite = useInviteUser((issued) => onHandout({ kind: 'invite', invite: issued }));
  /** An administrator of the shop sees the shop's people, never a superadministrator. */
  const rows = platform ? users : users.filter((user) => !user.is_superadmin);
  const [resetting, setResetting] = useState<AdminUser | null>(null);
  const [newPassword, setNewPassword] = useState('');

  function submitReset(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!resetting) return;
    const target = resetting;
    reset.mutate(
      { id: target.id, password: newPassword },
      {
        onSuccess: () => {
          onHandout({ kind: 'password', email: target.email, password: newPassword });
          setResetting(null);
          setNewPassword('');
        },
      },
    );
  }

  return (
    <div className="flex flex-col">
      <Table label="Usuarios" minWidth="0">
        <THead>
          <Tr>
            <Th>Correo</Th>
            <Th>Buzones</Th>
            <Th>Administra</Th>
            {platform ? <Th>Superadministrador</Th> : null}
            <Th>Estado</Th>
            <Th>Alta</Th>
            <Th>
              <span className="sr-only">Acciones</span>
            </Th>
          </Tr>
        </THead>
        <TBody>
          {rows.map((user) => (
            <Tr key={user.id} className={user.is_active ? undefined : 'text-ink-3'}>
              <Td className="align-middle">
                <span className="text-ink block leading-5 font-medium">{user.email}</span>
                {user.full_name ? (
                  <span className="text-ink-3 block text-[12.5px] leading-4">{user.full_name}</span>
                ) : null}
              </Td>
              <Td className="align-top">
                {user.is_superadmin ? (
                  <span className="text-ink-3 text-[13px]">todos</span>
                ) : (
                  <MailboxPicker
                    label={`Buzones de ${user.email}`}
                    known={known}
                    value={user.mailboxes ?? []}
                    disabled={update.isPending}
                    onChange={(next) =>
                      update.mutate({ id: user.id, payload: { mailboxes: next } })
                    }
                  />
                )}
              </Td>
              <Td className="align-middle">
                {user.is_superadmin ? (
                  <span className="text-ink-3 text-[13px]">plataforma</span>
                ) : (
                  <label className="text-ink-2 flex items-center gap-2 text-[13px]">
                    <input
                      type="checkbox"
                      className="accent-accent size-4"
                      aria-label={`Administra: ${user.email}`}
                      checked={user.is_admin}
                      disabled={!platform || update.isPending || user.id === me?.id}
                      onChange={(event) =>
                        update.mutate({
                          id: user.id,
                          payload: { is_admin: event.target.checked },
                        })
                      }
                    />
                    {user.is_admin ? 'Administrador' : 'No'}
                  </label>
                )}
              </Td>
              {platform ? (
                <Td className="align-middle">
                  <label className="text-ink-2 flex items-center gap-2 text-[13px]">
                    <input
                      type="checkbox"
                      className="accent-accent size-4"
                      aria-label={`Superadministrador: ${user.email}`}
                      checked={user.is_superadmin}
                      disabled={update.isPending || user.id === me?.id}
                      onChange={(event) =>
                        update.mutate({
                          id: user.id,
                          payload: { is_superadmin: event.target.checked },
                        })
                      }
                    />
                    {user.is_superadmin ? 'Sí' : 'No'}
                  </label>
                </Td>
              ) : null}
              <Td className="align-middle">
                <span className="flex flex-wrap gap-1">
                  {user.is_active ? null : <Badge tone="neutral">Desactivado</Badge>}
                  {user.must_change_password ? (
                    <Badge
                      tone="warn"
                      title="Aún no ha entrado con una contraseña suya: la elegirá con su enlace o en su primer acceso"
                    >
                      primer acceso pendiente
                    </Badge>
                  ) : null}
                </span>
              </Td>
              <Td className="align-middle whitespace-nowrap tabular-nums">
                {formatDate(dateFormat, user.created_at)}
              </Td>
              <Td className="text-right align-middle">
                <RowMenu
                  label={`Acciones de ${user.email}`}
                  items={[
                    {
                      label: user.is_active ? 'Desactivar' : 'Reactivar',
                      tone: user.is_active ? 'danger' : 'default',
                      disabled: update.isPending,
                      onSelect: () =>
                        update.mutate({ id: user.id, payload: { is_active: !user.is_active } }),
                    },
                    ...(platform
                      ? [
                          {
                            label: 'Nueva invitación',
                            disabled: !user.is_active || invite.isPending,
                            onSelect: () => invite.mutate(user.id),
                          },
                        ]
                      : []),
                    {
                      label: 'Nueva contraseña',
                      onSelect: () => {
                        setResetting(user);
                        setNewPassword('');
                      },
                    },
                  ]}
                />
              </Td>
            </Tr>
          ))}
        </TBody>
      </Table>
      {update.isError ? (
        <ErrorMessage error={update.error} className="m-4">
          No se pudo guardar el cambio
        </ErrorMessage>
      ) : null}
      {invite.isError ? (
        <ErrorMessage error={invite.error} className="m-4">
          No se pudo preparar la invitación
        </ErrorMessage>
      ) : null}

      {resetting ? (
        <form
          onSubmit={submitReset}
          className="bg-paper-2 border-line-2 flex flex-col gap-3 border-t p-4"
          aria-label={`Nueva contraseña para ${resetting.email}`}
        >
          <p className="text-ink text-[14px] font-medium">
            Nueva contraseña para {resetting.email}
          </p>
          <Input
            label="Contraseña"
            type="text"
            autoComplete="off"
            autoFocus
            value={newPassword}
            onChange={(event) => setNewPassword(event.target.value)}
            hint={`Mínimo ${MIN_PASSWORD} caracteres. Se enseñará una vez; la persona la cambiará al entrar y sus sesiones abiertas se cerrarán.`}
            minLength={MIN_PASSWORD}
            required
          />
          <div className="flex gap-2">
            <Button
              type="submit"
              size="sm"
              disabled={reset.isPending || newPassword.length < MIN_PASSWORD}
            >
              {reset.isPending ? 'Guardando…' : 'Guardar contraseña'}
            </Button>
            <Button type="button" size="sm" variant="ghost" onClick={() => setResetting(null)}>
              Cancelar
            </Button>
          </div>
          {reset.isError ? (
            <ErrorMessage error={reset.error}>No se pudo cambiar la contraseña</ErrorMessage>
          ) : null}
        </form>
      ) : null}
    </div>
  );
}

function NewUserForm({ onCreated }: { onCreated: (shown: Handout) => void }) {
  const platform = isPlatform(useSessionUser());
  const [email, setEmail] = useState('');
  const [fullName, setFullName] = useState('');
  const [isSuperadmin, setIsSuperadmin] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [mailboxes, setMailboxes] = useState<string[]>([]);
  const known = useKnownMailboxes();
  const [mode, setMode] = useState<HandoutMode>('invite');
  const [password, setPassword] = useState('');
  const create = useCreateUser();

  const passwordMissing = mode === 'password' && password.length < MIN_PASSWORD;

  function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const typed = password;
    const base = {
      email,
      full_name: fullName || null,
      is_superadmin: isSuperadmin,
      is_admin: isAdmin,
      mailboxes,
    };
    const payload = mode === 'invite' ? { ...base, invite: true } : { ...base, password: typed };
    create.mutate(payload, {
      onSuccess: (user) => {
        if (user.invite) onCreated({ kind: 'invite', invite: user.invite });
        else onCreated({ kind: 'password', email: user.email, password: typed });
        setEmail('');
        setFullName('');
        setIsSuperadmin(false);
        setIsAdmin(false);
        setMailboxes([]);
        setPassword('');
      },
    });
  }

  return (
    <Card
      title="Dar de alta a alguien"
      description="La persona entra con el enlace o la contraseña que le des y elige la suya."
    >
      <form onSubmit={submit} className="flex flex-col gap-3">
        <Input
          label="Correo"
          type="email"
          autoComplete="off"
          value={email}
          onChange={(event) => setEmail(event.target.value)}
          required
        />
        <Input
          label="Nombre"
          optional
          value={fullName}
          onChange={(event) => setFullName(event.target.value)}
        />
        {!isSuperadmin ? (
          <div className="flex flex-col gap-1">
            <span className="text-ink-2 text-[13px] font-medium">Buzones que lee</span>
            <MailboxPicker
              label="Buzones"
              known={known}
              value={mailboxes}
              onChange={setMailboxes}
            />
            <p className="text-ink-3 text-[12.5px] leading-snug">
              Verá esos buzones enteros y ninguno más. Sin buzón, la bandeja le sale vacía.
            </p>
          </div>
        ) : null}
        {platform ? (
          <label className="text-ink-2 flex items-start gap-2 text-[14px]">
            <input
              type="checkbox"
              className="accent-accent mt-0.5 size-4"
              checked={isSuperadmin}
              onChange={(event) => {
                setIsSuperadmin(event.target.checked);
                if (event.target.checked) setIsAdmin(false);
              }}
            />
            <span>
              Superadministrador
              <span className="text-ink-3 block text-[12.5px] leading-snug">
                Ignira: todos los buzones, Estadísticas, Accesos y Datos. Solo para quien mantiene
                la plataforma.
              </span>
            </span>
          </label>
        ) : null}
        {platform && !isSuperadmin ? (
          <label className="text-ink-2 flex items-start gap-2 text-[14px]">
            <input
              type="checkbox"
              className="accent-accent mt-0.5 size-4"
              checked={isAdmin}
              onChange={(event) => setIsAdmin(event.target.checked)}
            />
            <span>
              Administra el negocio
              <span className="text-ink-3 block text-[12.5px] leading-snug">
                Usuarios, buzones, destinos y activación, sea cual sea su rol. No amplía el correo
                que ve: solo sus buzones. Víctor y Ernesto.
              </span>
            </span>
          </label>
        ) : null}
        <div className="flex flex-col gap-1">
          <span className="text-ink-2 text-[13px] font-medium">Su primera contraseña</span>
          <Segmented<HandoutMode>
            label="Cómo recibe su primera contraseña"
            value={mode}
            onChange={setMode}
            options={[
              { value: 'invite', label: 'Con un enlace' },
              { value: 'password', label: 'La escribo yo' },
            ]}
          />
          <p className="text-ink-3 text-[12.5px] leading-snug">
            {mode === 'invite'
              ? 'Te damos un enlace que vale 24 horas y una sola vez. Se lo pasas y elige su contraseña.'
              : 'Escribes una contraseña provisional, se enseña una vez y la persona la cambia al entrar.'}
          </p>
        </div>
        {mode === 'password' ? (
          <Input
            label="Primera contraseña"
            type="text"
            autoComplete="off"
            value={password}
            onChange={(event) => setPassword(event.target.value)}
            minLength={MIN_PASSWORD}
            hint={`Mínimo ${MIN_PASSWORD} caracteres.`}
            required
          />
        ) : null}
        <Button type="submit" disabled={create.isPending || passwordMissing}>
          {create.isPending
            ? 'Dando de alta…'
            : mode === 'invite'
              ? 'Dar de alta y generar enlace'
              : 'Dar de alta'}
        </Button>
        {create.isError ? (
          <p role="alert" className="text-danger text-[13.5px]">
            {errorMessage(create.error)}
          </p>
        ) : null}
      </form>
    </Card>
  );
}
